import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CombinedStates } from 'core/types';
import useRequestFlow from 'core/guestBooking/useRequestFlow';
import VisitorList from 'components/Form/VisitorList';
import VisitorSiteAccess from 'components/Form/VisitorSiteAccess';
import { SiteAccess, VisitRequestFormResponsePayload } from 'core/visitRequestForm/types';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import SiteAccessNotifications from 'components/Form/SiteAccessNotifications';
import { unwrapResult } from '@reduxjs/toolkit';
import { isEmpty } from 'underscore';

interface Visitor {
    id: number;
    name: string;
    email?: string;
}

const SiteAccessForm: React.FC = () => {
    const [savedShiftDates, setSavedShiftDates] = useState<SiteAccess[]>([]);
    const [visitorsToNotify, setVisitorsToNotify] = useState<number[]>([]);

    const { stepData, visitRequest } = useSelector((state: CombinedStates) => state.visitRequestForm);
    const { id } = useParams<{ id: string }>();
    const flow = useRequestFlow();

    const { state: navState } = useLocation();
    const amending = navState?.amending;

    const navigation = useNavigate();

    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!amending && !flow.guestMode) ? visitRequest.encryptedId : null,
        formBasePath: '/visitor-request/',
        deleteEndpoint: 'visit-request',
    });

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(3, navigationState);
    };

    const handleInitialLoad = () => {
        if (id !== undefined || flow.guestMode) {
            flow.loadStep(4, amending);
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Visit Request',
                description: 'Please select a visit request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    useEffect(() => {
        if (visitRequest && stepData.visitors?.length) {
            if (!isEmpty(savedShiftDates)) {
                return;
            }
            setSavedShiftDates(visitRequest.siteAccess);
            const initialVisitorsToNotify = stepData.visitors
                .filter(visitor => visitor.notificationsEnabled)
                .map(visitor => visitor.id);
            setVisitorsToNotify(initialVisitorsToNotify);
        }
    }, [visitRequest, stepData]);

    let visitors: Visitor[] = [];
    if (stepData.visitors !== undefined) {
        visitors = stepData.visitors.map((visitor) => ({
            id: visitor.id,
            name: visitor.name,
            email: visitor.email,
        })).sort((a, b) => a.name.localeCompare(b.name));
    }

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        const { dates } = stepData;
        if (dates?.length !== savedShiftDates.length) {
            notification.error({
                message: 'Invalid Site Access',
                description: 'Please select site access for all dates',
            });
            return;
        }

        // A visitor with no email can't be notified, so never flag one — a
        // stale id can otherwise survive in visitorsToNotify (e.g. the visitor
        // had notifications on before their email was removed).
        const visitorsToDispatch = visitors.map((visitor) => ({
            id: visitor.id,
            notificationsEnabled: !!visitor.email && visitorsToNotify.includes(visitor.id),
        }));

        if (id !== undefined || flow.guestMode) {
            flow.updateStep(4, {
                visitors: visitorsToDispatch,
                visitorAccess: savedShiftDates,
                amending,
                forcedStep: 4,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(5, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const updateSavedAreaAccess = (values: SiteAccess[]) => {
        setSavedShiftDates(values);
    };

    const updateVisitorNotifications = (visitorIds: number[]) => {
        setVisitorsToNotify(visitorIds);
    };

    return (
        <RequestFormLayout
            currentStep={4}
            isNextEnabled
            isBackEnabled
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}>
            <>
                <VisitorList visitors={visitors} />
                <VisitorSiteAccess
                    siteAreas={stepData.areas || []}
                    savedShiftDates={savedShiftDates}
                    handleSave={updateSavedAreaAccess} />
                <SiteAccessNotifications
                    visitors={visitors}
                    selectedVisitors={visitorsToNotify}
                    onSelect={updateVisitorNotifications} />
            </>
        </RequestFormLayout>
    );
};

export default SiteAccessForm;
